import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    NgZone,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SIGNED_IN_SPEECH, SpeechTarget, SpeechTranscriptionService } from 'app/core/chat/speech-transcription.service';
import { Subscription } from 'rxjs';

import {
    formatVoiceClock,
    insertTranscript,
    microphoneMessage,
    supportMessage,
    uploadFailure,
    VOICE_MESSAGES,
    VoiceMessageKey,
} from './voice-input.util';
import {
    classifyMicrophoneError,
    VOICE_MAX_DURATION_MS,
    VOICE_MIN_DURATION_MS,
    VOICE_SILENCE_PEAK,
    VoiceClip,
    VoiceRecorder,
    voiceInputSupport,
} from './voice-recorder';

type VoiceState = 'idle' | 'starting' | 'recording' | 'transcribing';

/**
 * Microphone button for any agent chat composer. Tap to record, tap again to stop; the clip is
 * transcribed by the server and the text is typed into `target` at the caret, never sent. The user
 * reviews and edits it, then sends as usual.
 *
 * Drop it next to a composer's textarea and pass the textarea as `target`: the transcript arrives as an
 * ordinary `input` event, so `ngModel`, `[value]` with `(input)` and signal-fed composers all update
 * with no host code. `transcribed` also fires, for a host that would rather place the text itself.
 */
@Component({
    selector: 'app-voice-input',
    standalone: true,
    imports: [MatIconModule, MatProgressSpinnerModule, MatTooltipModule, TranslocoModule],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
        app-voice-input {
            position: relative;
            display: inline-flex;
            align-items: center;
            flex: 0 0 auto;
        }
        .voice-input {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
        }
        .voice-input__btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            inline-size: 2.5rem;
            block-size: 2.5rem;
            border: 0;
            border-radius: var(--sai-radius-full);
            background: transparent;
            color: var(--sai-text-secondary);
            cursor: pointer;
            transition: background-color 0.15s ease, color 0.15s ease;
        }
        .voice-input__btn:hover:not([aria-disabled='true']):not([disabled]) {
            background: var(--sai-surface-alt);
            color: var(--sai-text);
        }
        .voice-input__btn:focus-visible {
            outline: 2px solid var(--c1, #3dc3fb);
            outline-offset: 2px;
        }
        .voice-input__btn[aria-disabled='true'],
        .voice-input__btn[disabled] {
            opacity: 0.45;
            cursor: not-allowed;
        }
        /* Fuse paints every uncoloured mat-icon grey, which beats the button's own colour; without this
           the stop glyph is grey on red and the hover colour never reaches any icon. */
        .voice-input .voice-input__btn .mat-icon {
            color: inherit;
        }
        /* The page surface on solid danger, not white: dark mode's danger is a light red (#f87171) that
           white text fails contrast on, while the navy surface reads clearly. Light mode stays white. */
        .voice-input__btn--stop,
        .voice-input__btn--stop:hover:not([disabled]) {
            background: var(--sai-danger);
            color: var(--sai-surface);
        }
        .voice-input__btn--stop:hover:not([disabled]) {
            filter: brightness(0.92);
        }
        .voice-input__live {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding-inline: 0.5rem;
            block-size: 2rem;
            border-radius: var(--sai-radius-full);
            background: var(--sai-danger-soft);
            color: var(--sai-danger-soft-text);
            font-size: 0.8125rem;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }
        .voice-input__live--ending {
            background: var(--sai-warning-soft);
            color: var(--sai-warning-soft-text);
        }
        .voice-input__dot {
            inline-size: 0.5rem;
            block-size: 0.5rem;
            border-radius: var(--sai-radius-full);
            background: currentColor;
            animation: voice-input-pulse 1.2s ease-in-out infinite;
        }
        .voice-input__level {
            display: inline-block;
            inline-size: 2.25rem;
            block-size: 0.25rem;
            border-radius: var(--sai-radius-full);
            background: color-mix(in srgb, currentColor 20%, transparent);
            overflow: hidden;
        }
        .voice-input__level > span {
            display: block;
            block-size: 100%;
            background: currentColor;
            transition: inline-size 0.1s linear;
        }
        .voice-input__bubble {
            position: absolute;
            inset-block-end: calc(100% + 0.5rem);
            inset-inline-end: 0;
            z-index: 30;
            inline-size: max-content;
            max-inline-size: min(20rem, 80vw);
            padding: 0.625rem 0.75rem;
            border: 1px solid var(--sai-border);
            border-radius: var(--sai-radius-lg);
            background: var(--sai-surface);
            color: var(--sai-text);
            box-shadow: var(--sai-shadow-md);
            font-size: 0.8125rem;
            line-height: 1.4;
            text-align: start;
        }
        .voice-input__bubble-actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-block-start: 0.5rem;
        }
        .voice-input__link {
            padding: 0.125rem 0.5rem;
            border: 0;
            border-radius: var(--sai-control-radius);
            background: transparent;
            color: var(--c1, #3dc3fb);
            font-size: 0.8125rem;
            font-weight: 600;
            cursor: pointer;
        }
        .voice-input__link:focus-visible {
            outline: 2px solid var(--c1, #3dc3fb);
            outline-offset: 1px;
        }
        @keyframes voice-input-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
        }
        @media (prefers-reduced-motion: reduce) {
            .voice-input__dot { animation: none; }
            .voice-input__btn, .voice-input__level > span { transition: none; }
        }
        `,
    ],
    template: `
        <div class="voice-input" *transloco="let t">
            @switch (state) {
                @case ('recording') {
                    <button type="button" class="voice-input__btn" (click)="cancel()" [attr.aria-label]="t('Cancel recording')" [matTooltip]="t('Cancel recording')">
                        <mat-icon svgIcon="heroicons_outline:x-mark"></mat-icon>
                    </button>
                    <span class="voice-input__live" [class.voice-input__live--ending]="ending" aria-hidden="true">
                        <span class="voice-input__dot"></span>
                        <span>{{ clock }}</span>
                        <span class="voice-input__level"><span [style.inline-size.%]="levelPercent"></span></span>
                    </span>
                    <button type="button" class="voice-input__btn voice-input__btn--stop" (click)="finish()" [attr.aria-label]="t('Stop recording and transcribe')" [matTooltip]="t('Stop recording and transcribe')">
                        <mat-icon svgIcon="heroicons_solid:stop"></mat-icon>
                    </button>
                }
                @case ('transcribing') {
                    <button type="button" class="voice-input__btn" (click)="cancel()" [attr.aria-label]="t('Cancel transcription')" [matTooltip]="t('Cancel transcription')">
                        <mat-progress-spinner mode="indeterminate" diameter="20" strokeWidth="2.5"></mat-progress-spinner>
                    </button>
                }
                @default {
                    <button
                        type="button"
                        class="voice-input__btn"
                        [disabled]="disabled || state === 'starting'"
                        [attr.aria-disabled]="unsupportedReason ? 'true' : null"
                        [attr.aria-label]="t('Voice input')"
                        [attr.aria-describedby]="message ? bubbleId : null"
                        [matTooltip]="unsupportedReason ? t(unsupportedReason) : t('Voice input')"
                        (click)="begin()"
                    >
                        <mat-icon svgIcon="heroicons_outline:microphone"></mat-icon>
                    </button>
                }
            }
            @if (message) {
                <div class="voice-input__bubble" [id]="bubbleId" role="status" dir="auto">
                    <div>{{ t(message) }}</div>
                    <div class="voice-input__bubble-actions">
                        @if (retryClip) {
                            <button type="button" class="voice-input__link" (click)="retry()">{{ t('Try again') }}</button>
                        }
                        <button type="button" class="voice-input__link" (click)="dismiss()">{{ t('Close') }}</button>
                    </div>
                </div>
            }
            <span class="sr-only" aria-live="polite">{{ announcement ? t(announcement) : '' }}</span>
        </div>
    `,
})
export class VoiceInputButtonComponent {
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _zone = inject(NgZone);
    private readonly _speech = inject(SpeechTranscriptionService);
    private readonly _transloco = inject(TranslocoService);

    /** The message box the transcript is typed into. */
    @Input() target: HTMLTextAreaElement | HTMLInputElement | null = null;
    @Input() disabled = false;
    /** Signed-in chats leave this alone; a public agent chat passes its session. */
    @Input() speechTarget: SpeechTarget = SIGNED_IN_SPEECH;

    /** The transcript, after it has been typed into `target`. */
    @Output() readonly transcribed = new EventEmitter<string>();

    private static _nextId = 0;
    readonly bubbleId = `voice-input-msg-${VoiceInputButtonComponent._nextId++}`;

    state: VoiceState = 'idle';
    clock = formatVoiceClock(0);
    levelPercent = 0;
    ending = false;
    message: VoiceMessageKey | null = null;
    announcement = '';
    /** The last clip that failed in a way a resend could fix; cleared on success, cancel or dismiss. */
    retryClip: VoiceClip | null = null;
    readonly unsupportedReason = supportMessage(voiceInputSupport());

    private _recorder: VoiceRecorder | null = null;
    private _upload: Subscription | null = null;

    constructor() {
        inject(DestroyRef).onDestroy(() => this._teardown());
    }

    /** Escape backs out of a recording or a pending transcription, wherever focus is inside the control. */
    @HostListener('keydown.escape', ['$event'])
    onEscape(event: Event): void {
        if (this.state === 'recording' || this.state === 'transcribing') {
            event.stopPropagation();
            this.cancel();
        } else if (this.message) {
            event.stopPropagation();
            this.dismiss();
        }
    }

    async begin(): Promise<void> {
        if (this.unsupportedReason) {
            this._show(this.unsupportedReason);
            return;
        }
        if (this.disabled || this.state !== 'idle') {
            return;
        }
        this.message = null;
        this.retryClip = null;
        this.state = 'starting';
        this._cdr.markForCheck();
        // The recorder ticks ten times a second; running it outside Angular keeps that from checking
        // the whole page each time. Only this component is marked for check.
        const recorder = new VoiceRecorder({
            onTick: (elapsed, level) =>
                this._zone.run(() => {
                    this.clock = formatVoiceClock(elapsed);
                    this.levelPercent = Math.min(100, Math.round(level * 160));
                    this.ending = VOICE_MAX_DURATION_MS - elapsed <= 10_000;
                    this._cdr.markForCheck();
                }),
            onLimit: () => this._zone.run(() => this.finish()),
        });
        try {
            await this._zone.runOutsideAngular(() => recorder.start());
        } catch (err) {
            recorder.cancel();
            this.state = 'idle';
            this._show(microphoneMessage(classifyMicrophoneError(err)));
            return;
        }
        this._recorder = recorder;
        this.state = 'recording';
        this.clock = formatVoiceClock(0);
        this.announcement = 'Recording. Press stop when you are done.';
        this._cdr.markForCheck();
    }

    async finish(): Promise<void> {
        const recorder = this._recorder;
        if (!recorder || this.state !== 'recording') {
            return;
        }
        this._recorder = null;
        let clip: VoiceClip;
        try {
            clip = await recorder.stop();
        } catch {
            this.state = 'idle';
            this._show(VOICE_MESSAGES.micFailed);
            return;
        }
        if (clip.durationMs < VOICE_MIN_DURATION_MS || !clip.blob.size) {
            this.state = 'idle';
            this._show(VOICE_MESSAGES.tooShort);
            return;
        }
        if (clip.peak !== null && clip.peak < VOICE_SILENCE_PEAK) {
            this.state = 'idle';
            this._show(VOICE_MESSAGES.silent);
            return;
        }
        this._send(clip);
    }

    retry(): void {
        const clip = this.retryClip;
        if (clip && this.state === 'idle') {
            this.message = null;
            this._send(clip);
        }
    }

    cancel(): void {
        this._teardown();
        this.state = 'idle';
        this.retryClip = null;
        this.message = null;
        this.announcement = 'Voice input cancelled.';
        this._cdr.markForCheck();
    }

    dismiss(): void {
        this.message = null;
        this.retryClip = null;
        this._cdr.markForCheck();
    }

    private _send(clip: VoiceClip): void {
        this.state = 'transcribing';
        this.announcement = 'Transcribing your recording.';
        this._cdr.markForCheck();
        this._upload = this._speech.transcribe(clip.blob, clip.durationMs, this._transloco.getActiveLang(), this.speechTarget).subscribe({
            next: (result) => {
                this._upload = null;
                this.state = 'idle';
                this.retryClip = null;
                if (this.target) {
                    insertTranscript(this.target, result.text);
                    this.target.focus();
                }
                this.transcribed.emit(result.text);
                this.announcement = 'Transcript added to the message box. Review it, then send.';
                this._cdr.markForCheck();
            },
            error: (err: unknown) => {
                this._upload = null;
                this.state = 'idle';
                const failure = uploadFailure(err);
                this.retryClip = failure.retryable ? clip : null;
                this._show(failure.message);
            },
        });
    }

    private _show(message: VoiceMessageKey): void {
        this.message = message;
        this.announcement = '';
        this._cdr.markForCheck();
    }

    private _teardown(): void {
        this._recorder?.cancel();
        this._recorder = null;
        this._upload?.unsubscribe();
        this._upload = null;
    }
}
